import React from 'react';
import {useIntl} from 'react-intl';

import {
  AlertEpisodeDispositionPostDto,
  AlertEpisodeGroupId,
  AlertEpisodeGroupDto,
  AlertEpisodeRowDto,
  AlertSnoozeOptionDto,
} from './alert_types';
import {AlertCriteriaRenderer} from './AlertCriteriaChips';
import {AlertEpisodeRow} from './AlertEpisodeRow';
import {LightPanelGroup} from '../LightPanelGroup';

type Props = {
  group: AlertEpisodeGroupDto;
  /** Key prefix of the persisted open/closed state (per page). */
  storageIdPrefix: string;
  first?: boolean;
  /** Opens the group as a click on its header would (it holds an episode the panel brings into view). */
  forceOpen?: boolean;
  snoozeOptions: AlertSnoozeOptionDto[];
  onSnoozeOptionsNeeded?: () => void;
  onAction: (episodeId: number, post: AlertEpisodeDispositionPostDto) => void;
  actionsDisabled?: boolean;
  systemActorName: string;
  renderCriteria?: AlertCriteriaRenderer;
  /** Where a row's name leads; no links without it. */
  getEpisodeHref?: (row: AlertEpisodeRowDto) => string | null;
};

const GROUP_ICONS: Record<AlertEpisodeGroupId, {iconName: string; viewBox: string}> = {
  IN_FOCUS: {iconName: 'bedIcon', viewBox: '0 0 16 16'},
  ELSEWHERE: {iconName: 'hospitalIcon', viewBox: '0 0 640 512'},
  NOT_ACTIVE: {iconName: 'bellSlashIcon', viewBox: '0 0 640 512'},
};
/** Not active is collapsed by default (PDW-182), the two active groups open. */
const DEFAULT_OPEN: Record<AlertEpisodeGroupId, boolean> = {IN_FOCUS: true, ELSEWHERE: true, NOT_ACTIVE: false};

/**
 * One collapsible group of the Alerts panel: In this hospitalization, Elsewhere for this patient, Not active.
 * The count is the rows in the group (snoozed included) as the BE sends it. Deleted episodes stay in Not
 * active, hidden only by the collapse.
 */
export const AlertEpisodeGroup: React.FC<Props> = (props) => {
  const {
    group,
    storageIdPrefix,
    first,
    forceOpen,
    snoozeOptions,
    onSnoozeOptionsNeeded,
    onAction,
    actionsDisabled,
    systemActorName,
    renderCriteria,
    getEpisodeHref,
  } = props;
  const intl = useIntl();
  const {iconName, viewBox} = GROUP_ICONS[group.group];
  return (
    <LightPanelGroup
      iconName={iconName}
      viewBox={viewBox}
      label={intl.formatMessage({id: `alert.panel.group.${group.group}`})}
      count={group.count}
      storageKey={`${storageIdPrefix}.alerts.group.${group.group}`}
      defaultOpen={DEFAULT_OPEN[group.group]}
      first={first}
      forceOpen={forceOpen}
      dataName={'dl-alert-episode-group'}
      groupId={group.group}
    >
      {group.rows.map((row) => (
        <AlertEpisodeRow
          key={row.episodeId}
          row={row}
          snoozeOptions={snoozeOptions}
          onSnoozeOptionsNeeded={onSnoozeOptionsNeeded}
          onAction={onAction}
          actionsDisabled={actionsDisabled}
          systemActorName={systemActorName}
          renderCriteria={renderCriteria}
          href={getEpisodeHref?.(row) ?? null}
        />
      ))}
    </LightPanelGroup>
  );
};

AlertEpisodeGroup.displayName = 'AlertEpisodeGroup';
